import ky from 'ky';
import type { Options } from 'ky';

// Utility function to get CSRF token from cookies
export function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return value;
    }
  }
  return null;
}

// Shared KY options with CSRF and auth handling
export const kyOptions: Options = {
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
  },
  throwHttpErrors: false,
  hooks: {
    beforeRequest: [
      async (request) => {
        const method = request.method.toLowerCase();
        const isModifyingOperation = ['post', 'put', 'patch', 'delete'].includes(method);

        if (isModifyingOperation) {
          const csrfToken = getCsrfToken();
          if (csrfToken) {
            request.headers.set('X-CSRFToken', csrfToken);
          }
        }

        return request;
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        const status = response.status;

        if (status === 401 || status === 403) {
          // Clear authentication state
          localStorage.removeItem('refine-auth');
          localStorage.removeItem('refine-user');

          // Clear all cookies
          document.cookie.split(";").forEach((cookie) => {
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          });

          // Redirect to login page
          window.location.replace('/login');

          // Return void to stop error propagation
          return;
        }

        return response;
      },
    ],
    beforeError: [
      async (error) => {
        const status = error.response?.status;

        if (status === 401 || status === 403) {
          // Clear authentication state
          localStorage.removeItem('refine-auth');
          localStorage.removeItem('refine-user');

          // Clear all cookies
          document.cookie.split(";").forEach((cookie) => {
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          });

          // Redirect to login page
          window.location.replace('/login');
        }

        return error;
      },
    ],
  },
};

// Shared KY instance for authenticated requests
export const authenticatedKy = ky.create(kyOptions);

// Authenticated fetch wrapper for blob/file downloads
// Use this for non-JSON responses
export async function authenticatedFetch(url: string, options?: RequestInit): Promise<Response> {
  // Use ky for the request (it has our auth hooks)
  return authenticatedKy(url, options as any).then(r => r);
}
